// Checklist items in a note, markdown style: `- [ ] to do` and `- [x] done`
// at the start of a line, indented or not. Lines inside a fenced block
// (a sheet, a recipe) are that block's, never items.
import { findFenced } from '@/app/fence';

export interface Item {
  // where the line starts, and its marker `- [ ] ` after the indent
  line: number;
  from: number;
  to: number;
  // the ' ' or 'x' between the brackets
  mark: number;
  checked: boolean;
}

const ITEM = /^([^\S\n]*)[-*] \[([ xX])\] /;

// the item on one line, positions from its start
export const parseItem = (text: string, line = 0): Item | null => {
  const m = ITEM.exec(text);
  if (!m) return null;
  const from = line + m[1].length;
  return {
    line,
    from,
    to: line + m[0].length,
    mark: from + 3,
    checked: m[2] !== ' ',
  };
};

export const findItems = (body: string): Item[] => {
  const fenced = findFenced(body, '\\w*');
  const starts = [0, ...Array.from(body.matchAll(/\n/g), m => m.index + 1)];
  return starts
    .filter(start => !fenced.some(f => start >= f.from && start <= f.to))
    .flatMap(start => {
      const end = body.indexOf('\n', start);
      const item = parseItem(body.slice(start, end === -1 ? undefined : end));
      return item
        ? [{
          ...item,
          line: start,
          from: start + item.from,
          to: start + item.to,
          mark: start + item.mark,
        }]
        : [];
    });
};

// the change that checks an unchecked item, and unchecks a checked one
export const toggle = (item: Item) => ({
  from: item.mark,
  to: item.mark + 1,
  insert: item.checked ? ' ' : 'x',
});

// the changes that leave every item checked, or every one unchecked
export const checkAll = (items: Item[], checked: boolean) =>
  items.filter(i => i.checked !== checked).map(toggle);

// A line turned into an unchecked item, a bullet it had becoming the box, or
// an item turned back into a plain line
export const toggleItem = (text: string) => {
  const item = parseItem(text);
  if (item) return text.slice(0, item.from) + text.slice(item.to);
  const [, indent, rest] = /^([^\S\n]*)(?:[-*] )?(.*)$/s.exec(text)!;
  return `${indent}- [ ] ${rest}`;
};

// What Enter at `at` in an item's line does, as a change to the line and
// where the cursor goes, positions from its start: the next item at the same
// indent, what's after the cursor going with it, or, on an item with nothing
// written, the end of the list, an empty line in its place. Null where Enter
// is just a new line - off an item, or with the cursor in its marker
export const nextItem = (text: string, at: number) => {
  const item = parseItem(text);
  if (!item || at < item.to) return null;
  if (!text.slice(item.to).trim()) {
    return { from: 0, to: text.length, insert: '', cursor: 0 };
  }
  const insert = `\n${text.slice(0, item.from)}- [ ] `;
  return { from: at, to: at, insert, cursor: at + insert.length };
};
